import { useEffect, useState } from "react";
import { IconArrowLeft, IconDeviceFloppy, IconDownload } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, Input, Text } from "@/components/ui";
import { BuilderSkeleton } from "./ReporteriaAdminSkeletons";
import { qk } from "@/lib/query-keys";
import { useConfirmDialog } from "@/lib/confirm-dialog-store";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { CatalogoTablas } from "@/features/reporteria/components/CatalogoTablas";
import { ColumnasSeleccionadas } from "@/features/reporteria/components/ColumnasSeleccionadas";
import { AjustesReporte } from "@/features/reporteria/components/AjustesReporte";
import { RelacionesYFormulas } from "@/features/reporteria/components/RelacionesYFormulas";
import { PreviewTabla } from "@/features/reporteria/components/PreviewTabla";
import { emptyDef, esEjecutable, hidratarEtiquetas } from "@/features/reporteria/state/builder-ops";
import type { RepDefinicion, ReporteDefaultDto } from "@/features/reporteria/services/reporteria.api";
import { defContar, defEncolarExportacion, defGuardar, defObtener, defValoresColumna } from "../services/admin-reporteria.api";
import { useDefCatalogo, useDefPreviewInfinito, useDefRelaciones } from "../hooks/use-def-builder";

/**
 * Builder de un reporte PREDEFINIDO en el contexto de una configuración (§8.2).
 * Reusa los componentes del builder de usuario (S11) pero con el catálogo, las
 * relaciones y el preview de ESA configuración (whitelist de una sola config).
 */
export function PredefinidosBuilder({
  idConfig,
  predefinido,
  onSalir,
}: {
  idConfig: number;
  predefinido: ReporteDefaultDto | null;
  onSalir: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirmDialog();
  const catalogo = useDefCatalogo(idConfig);
  const relaciones = useDefRelaciones(idConfig);

  const cargarDef = useQuery({
    queryKey: ["rep", "admin", "predefinido-def", predefinido?.id ?? 0],
    queryFn: () => defObtener(predefinido!.id),
    enabled: predefinido != null,
  });

  const [def, setDefState] = useState<RepDefinicion>(() => emptyDef());
  const setDef = (f: (d: RepDefinicion) => RepDefinicion) => setDefState((prev) => f(prev));
  const [nombre, setNombre] = useState(predefinido?.nombre ?? "Reporte predefinido");
  const [listo, setListo] = useState(predefinido == null);

  useEffect(() => {
    if (predefinido != null && cargarDef.data && !listo) {
      setDefState({ ...emptyDef(), ...cargarDef.data });
      setListo(true);
    }
  }, [predefinido, cargarDef.data, listo]);

  // Rellena las etiquetas legibles desde el catálogo (un predefinido guardado
  // puede volver sin etiquetas · evita mostrar tokens). Corre al llegar el
  // catálogo o al cargar/editar la definición; `hidratarEtiquetas` devuelve el
  // mismo objeto si no hay nada que rellenar, así que no genera bucles.
  useEffect(() => {
    if (catalogo.data) setDefState((d) => hidratarEtiquetas(d, catalogo.data!));
  }, [catalogo.data, def]);

  const activo = esEjecutable(def);
  const datosPreview = useDefPreviewInfinito(idConfig, def, activo);

  const guardar = useMutation({
    mutationFn: () => defGuardar(idConfig, { id: predefinido?.id ?? null, nombre, descripcion: predefinido?.descripcion ?? null, def }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.repAdminPredefinidos(idConfig) });
      toastExito({ title: predefinido ? "Predefinido actualizado" : "Predefinido creado" });
      onSalir();
    },
    onError: toastErrorApi,
  });

  const exportar = useMutation({
    mutationFn: () => defEncolarExportacion(idConfig, def, nombre.trim() || "Reporte"),
    onSuccess: () => toastExito({ title: "Exportación en proceso", message: "El archivo aparecerá en tus reportes generados." }),
    onError: toastErrorApi,
  });

  async function exportarConConfirmacion() {
    let n: number | null = null;
    try {
      n = await defContar(idConfig, def);
    } catch {
      n = null;
    }
    const ok = await confirm({
      title: "Exportar a Excel",
      message:
        n != null
          ? `Se generará un archivo con ${n.toLocaleString("es-MX")} fila(s). ¿Continuar?`
          : "Se generará el archivo de Excel. ¿Continuar?",
      confirmLabel: "Exportar",
    });
    if (ok) exportar.mutate();
  }

  if (catalogo.isLoading || !listo) return <BuilderSkeleton />;

  if (catalogo.isError || !catalogo.data) {
    return (
      <div className="p-4">
        <Text c="red">No se pudo cargar el catálogo de la configuración.</Text>
        <Button variant="default" className="mt-2" onClick={onSalir}>
          Volver
        </Button>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onSalir}>
          Volver a predefinidos
        </Button>
        <Input className="w-64" value={nombre} onChange={(e) => setNombre(e.currentTarget.value)} placeholder="Nombre del predefinido" />
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          loading={guardar.isPending}
          disabled={!esEjecutable(def) || !nombre.trim()}
          onClick={() => guardar.mutate()}
        >
          Guardar predefinido
        </Button>
        <Button
          variant="default"
          leftSection={<IconDownload size={16} />}
          loading={exportar.isPending}
          disabled={!esEjecutable(def)}
          onClick={exportarConConfirmacion}
        >
          Exportar a Excel
        </Button>
      </div>

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-h-0 lg:h-full">
          <CatalogoTablas catalogo={catalogo.data} def={def} setDef={setDef} />
        </div>
        <div className="min-h-0 space-y-3 overflow-y-auto lg:h-full">
          <ColumnasSeleccionadas
            def={def}
            setDef={setDef}
            fetchValores={(t) => defValoresColumna(idConfig, t, 50)}
            contexto={`def-${idConfig}`}
          />
          <AjustesReporte def={def} setDef={setDef} catalogo={catalogo.data} />
          <RelacionesYFormulas def={def} setDef={setDef} catalogo={catalogo.data} relaciones={relaciones.data ?? []} />
        </div>
        <div className="min-h-0 lg:h-full">
          <PreviewTabla activo={activo} datos={datosPreview} def={def} setDef={setDef} />
        </div>
      </div>
    </div>
  );
}
